import { incluirClubesNaCopa } from './copa';
import { paisDaLiga } from './ligas-mundo';
import { divisaoDoClube, divisaoDoHumano, tabela, tabelaDaLiga, type EstadoJogo } from './jogo';
import { Rng } from './rng';
import type { Clube } from './tipos';

/**
 * Carreira do técnico no jogo solo: terminar a temporada com o caixa em -R$ 10 milhões ou pior leva à demissão. O técnico então
 * escolhe uma proposta de um clube da CPU e segue o jogo por ele. (A demissão por confiança baixa do jogo online é outra regra,
 * em server/sala.ts.)
 */
export const SALDO_DEMISSAO = -10_000_000;
const PROPOSTAS = 3;

/** O caixa do clube do técnico está no limite de demissão (ou abaixo dele)? */
export function saldoDemissivel(e: EstadoJogo): boolean {
  if (e.semDemissaoPorCaixa) return false; // o jogador desligou a regra nas Opções
  const c = e.clubes.find((x) => x.id === e.humanoId);
  return !!c && c.caixa <= SALDO_DEMISSAO;
}

/**
 * Até 3 clubes da CPU que querem o técnico demitido, de preferência do mesmo tamanho ou menores (nunca o clube de onde saiu).
 * Sorteia com o gerador do jogo, então o resultado é reproduzível a partir do save.
 */
export function propostasDeEmprego(e: EstadoJogo): number[] {
  const rng = new Rng(e.rngEstado);
  const origem = e.clubes.find((c) => c.id === e.humanoId);
  const candidatos = e.clubes.filter((c) => !c.humano && c.id !== e.humanoId);
  const parecidos = origem ? candidatos.filter((c) => c.nivel <= origem.nivel + 2) : candidatos;
  const base = parecidos.length >= PROPOSTAS ? parecidos : candidatos;
  const escolhidos = rng.embaralhar(base.map((c) => c.id)).slice(0, PROPOSTAS);
  e.rngEstado = rng.estado;
  return escolhidos;
}

/** País do clube: o da liga estrangeira em que ele joga ou o Brasil. */
function paisDoClube(c: Clube): string {
  return c.liga ? (paisDaLiga(c.liga)?.nome ?? c.liga) : 'Brasil';
}

/**
 * Convites de emprego no fim da temporada. Campeão de qualquer campeonato (liga, copa, Libertadores...) recebe 3 convites de clubes
 * sorteados de ligas de outros países (só num jogo com as ligas estrangeiras no save). Quem não foi campeão mas terminou entre os 3 primeiros da liga recebe, com chance, convites de clubes maiores
 * (de qualquer país). Usa um gerador próprio derivado do estado, então o resultado é sempre o mesmo para a
 * mesma temporada (recarregar ou reabrir a janela não sorteia de novo) e a sequência do jogo não muda.
 */
export function convitesDeEmprego(e: EstadoJogo, clubeId: number = e.humanoId): number[] {
  if (e.formato === 'copa') return [];
  const origem = e.clubes.find((c) => c.id === clubeId);
  if (!origem) return [];
  // cada técnico (online) tem o seu sorteio; no solo a semente é a de sempre
  const rng = new Rng((e.rngEstado ^ 0x9e3779b9 ^ (e.temporada * 2654435761) ^ (clubeId === e.humanoId ? 0 : clubeId * 40503)) >>> 0);
  // campeão de qualquer campeonato na temporada (liga, copa, Libertadores...): 3 convites de clubes sorteados de ligas de outros países
  if ((origem.trofeus ?? []).some((t) => t.temporada === e.temporada)) {
    const pais = paisDoClube(origem);
    const estrangeiros = e.clubes.filter((c) => !c.humano && c.id !== origem.id && paisDoClube(c) !== pais);
    if (estrangeiros.length >= PROPOSTAS) return rng.embaralhar(estrangeiros.map((c) => c.id)).slice(0, PROPOSTAS);
  }
  // classificação da liga do clube (o jogo com o mundo inteiro tem uma tabela por liga)
  const tabelaDele = origem.liga && e.mundo ? tabelaDaLiga(e, origem.liga) : e.acesso ? tabela(e, divisaoDoClube(e, origem)) : tabela(e);
  const posicao = tabelaDele.findIndex((l) => l.clubeId === origem.id) + 1;
  if (posicao < 1 || posicao > 3) return [];
  if (!rng.chance(posicao === 1 ? 0.85 : 0.5)) return [];
  const maiores = e.clubes.filter((c) => !c.humano && c.id !== origem.id && c.nivel > origem.nivel);
  return rng.embaralhar(maiores.map((c) => c.id)).slice(0, PROPOSTAS);
}

/** O técnico passa a comandar `clubeId`; o clube antigo vira da CPU. Os dois ficam com a confiança renovada. */
export function assumirClube(e: EstadoJogo, clubeId: number): Clube {
  const novo = e.clubes.find((c) => c.id === clubeId);
  if (!novo) throw new Error('clube não encontrado');
  if (novo.humano && novo.id !== e.humanoId) throw new Error('esse clube já tem técnico');
  const antigo = e.clubes.find((c) => c.id === e.humanoId);
  if (antigo && antigo.id !== novo.id) {
    antigo.humano = false;
    antigo.confiancaDiretoria = 60;
    antigo.confiancaTorcida = 60;
  }
  novo.humano = true;
  novo.confiancaDiretoria = 70;
  novo.confiancaTorcida = 70;
  e.humanoId = novo.id;
  if (e.acesso) e.divisao = divisaoDoHumano(e) === 2 ? 'b' : 'a';
  // o clube de técnico humano sempre disputa a Copa (a Copa nova só nasce no encerramento da temporada, que já cuida disso)
  try {
    incluirClubesNaCopa(e, [novo.id]);
  } catch {
    /* sem vaga para trocar: segue */
  }
  return novo;
}
